import { type FormEvent, useEffect, useRef, useState } from "react";
import styles from "./Login.module.css";
import { loginError } from "./loginError.js";

/**
 * The way in.
 *
 * A username and a password, no "remember me", no third-party sign-in (design/README.md
 * §Settings). The whole screen is the wordmark and two fields.
 *
 * The design said one password field, "because there is one account and he knows who he is". The
 * owner asked for both, and the reasoning holds: the username is not a secret and does not make
 * the password harder to guess, but it does make the endpoint refuse a request carrying only a
 * password — the shape every automated tool sends at a login it has never seen. One field is a
 * cheap price for that.
 *
 * Errors are stated in the interface's own voice, inline, in Plex Mono — no icon, no box, no
 * red-bordered alert component (design/README.md §Errors). Which error, and the reasoning behind
 * each one, lives in ./loginError.ts.
 */

export interface LoginProps {
  onLogin: (username: string, password: string) => Promise<void>;
}

export function Login({ onLogin }: LoginProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const first = useRef<HTMLInputElement>(null);
  const passwordField = useRef<HTMLInputElement>(null);

  // The top of the form, so the screen starts where typing starts.
  useEffect(() => first.current?.focus(), []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || username.trim() === "" || password === "") return;

    setBusy(true);
    setError(null);
    try {
      await onLogin(username.trim(), password);
    } catch (cause) {
      const { status, code } = cause as { status?: number; code?: string };
      setError(loginError(status ?? 0, code, globalThis.location?.origin));
      // The password is cleared and the username is not: retyping an email address you already
      // typed correctly is a punishment for a mistake that was probably not there.
      setPassword("");
      passwordField.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.page} data-testid="login">
      <form className={styles.form} onSubmit={submit}>
        <div className={styles.wordmark}>
          TRACK<span>MARK</span>
        </div>

        <label className={styles.label} htmlFor="username">
          USERNAME
        </label>
        <input
          ref={first}
          id="username"
          type="text"
          className={styles.field}
          data-testid="login-username"
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />

        <label className={styles.label} htmlFor="password">
          PASSWORD
        </label>
        <input
          ref={passwordField}
          id="password"
          type="password"
          className={styles.field}
          data-testid="login-password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <button
          type="submit"
          className={styles.submit}
          data-testid="login-submit"
          disabled={busy || username.trim() === "" || password === ""}
        >
          {busy ? "…" : "Enter"}
        </button>

        {error && (
          <div className={styles.error} data-testid="login-error" role="alert">
            {error}
          </div>
        )}
      </form>
    </div>
  );
}
